// Quellen-agnostisches Produkt-DTO + Mapper.
// Die UI rendert NUR dieses DTO und kennt weder SAP-Tabellen noch BAPIs.
// Siehe Requirements_Promts/gineex_produktdetail_prompt_17.06.2026.md.
// Interne Shop-Produkte: Mapping aus der DB (inkl. der reichen Detail-Felder).
// SAP-Produkte: später über den Adapter — gleiches DTO, ohne UI-Änderung.

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

// Bestands-Status auf die UI-Ampel (green/yellow/red) normalisieren.
// DB/SAP nutzt ok/low/empty; akzeptiert auch bereits gesetzte Ampelfarben.
// Ohne brauchbaren Status: aus der Menge ableiten.
const STATUS_COLOR = { ok: 'green', low: 'yellow', empty: 'red', green: 'green', yellow: 'yellow', red: 'red' };
const normStockStatus = (status, qty) =>
  STATUS_COLOR[status] || ((toNum(qty) ?? 0) > 0 ? 'green' : 'red');

// Sicheres Parsen der als JSON-String gespeicherten Detail-Blöcke (LONGTEXT-Spalten).
const safeParse = (v, fallback) => {
  if (v === null || v === undefined || v === '') return fallback;
  if (typeof v === 'object') return v;
  try {
    const parsed = JSON.parse(v);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
};

// Reihenfolge der Blöcke je viewProfile — gleiche Daten, nur andere Anordnung.
export const blockOrder = (viewProfile) =>
  viewProfile === 'buyer'
    ? ['procurement', 'availability', 'technical', 'compliance', 'documents']
    : ['technical', 'availability', 'documents', 'procurement', 'compliance'];

// F11: Default-Sichtprofil je Rolle — Einkauf (purchaser/supervisor) -> 'buyer',
// sonst (admin/Technik/unbekannt) -> 'technician'. Der Toggle ueberschreibt es.
export const defaultProfileForRole = (role) =>
  role === 'buyer' || role === 'purchaser' || role === 'supervisor' ? 'buyer' : 'technician';

// i18n-Key für die Preis-Herkunft (nie ein Rohpreis ohne Herkunft).
export const priceSourceKey = (source) => `productDetail.priceSource.${source || 'estimate'}`;

// Waehlt Name/Beschreibung aus dem texts[]-Array fuer die angegebene Sprache.
// Fallback-Kette: gewuenschte Sprache → 'de' → erster Eintrag → null.
const pickTexts = (texts, lang) => {
  if (!Array.isArray(texts) || texts.length === 0) return null;
  const byLang = texts.find((t) => t.lang === lang);
  if (byLang) return byLang;
  const byDe = texts.find((t) => t.lang === 'de');
  if (byDe) return byDe;
  return texts[0];
};

// Mappt eine einzelne Bestellhistorie-Zeile (snake_case DB-Row) auf camelCase DTO.
const mapOrderHistory = (o) => ({
  orderNo: o.order_no || '',
  orderItem: o.order_item || '',
  orderDate: o.order_date || '',
  vendor: o.vendor || '',
  vendorName: o.vendor_name || o.vendor || '',
  quantity: toNum(o.quantity) ?? 0,
  unit: o.unit || '',
  netPrice: toNum(o.net_price),
  priceUnit: toNum(o.price_unit) ?? 1,
  netValue: toNum(o.net_value),
  currency: o.currency || 'EUR',
  plant: o.plant || '',
  docType: o.doc_type || '',
});

// Mappt eine einzelne Bezugsquelle (snake_case DB-Row) auf camelCase DTO.
// Inklusive Staffeln (scales[]).
const mapSource = (s) => ({
  vendor: s.vendor || '',
  vendorName: s.vendor_name || '',
  vendorMaterialNo: s.vendor_material_no || '',
  infoRecordNo: s.info_record_no || '',
  purchasingOrg: s.purchasing_org || '',
  purchasingGroup: s.purchasing_group || '',
  netPrice: toNum(s.net_price),
  currency: s.currency || 'EUR',
  priceUnit: toNum(s.price_unit) ?? 1,
  orderUnit: s.order_unit || '',
  minOrderQty: toNum(s.min_order_qty) ?? 1,
  plannedDeliveryTime: toNum(s.planned_delivery_time),
  incoterms: s.incoterms || '',
  isPrimary: Boolean(s.is_primary),
  scales: Array.isArray(s.scales)
    ? s.scales.map((sc) => ({
        minQty: toNum(sc.min_qty) ?? 0,
        amount: toNum(sc.amount) ?? 0,
        currency: sc.currency || 'EUR',
        priceUnit: toNum(sc.price_unit) ?? 1,
      }))
    : [],
});

// Baut das DTO aus einem internen Shop-Produkt (DB-Row, inkl. reicher Detail-Felder).
// Unterstuetzt neue SAP-Felder: source, ean, price_unit, material_type, status,
// gross_weight, net_weight, weight_unit, size_text, profit_center,
// texts[] (MAKT, mehrsprachig), stock[] (MARD, Multi-Lagerort),
// sources[] (product_sources + product_price_scales, Bezugsquellen).
export const buildProductDto = (p, currentLang = 'de') => {
  if (!p) return null;

  // Attribute: child-table (p.attributes[]) bevorzugen; Fallback auf JSON-Spalte (Rückwärtskompatibilität)
  const childAttributes = Array.isArray(p.attributes) && p.attributes.length > 0
    ? p.attributes.map((a) => ({ label: a.label || a.char_name, value: a.value, unit: a.unit }))
    : null;
  const techAttrs = childAttributes !== null ? childAttributes : safeParse(p.technical_attributes, []);
  const scales = safeParse(p.price_scales, []);
  const contract = safeParse(p.contract_data, { exists: false });
  const docs = safeParse(p.documents, []);
  const comp = safeParse(p.compliance, []);

  // Mehrsprachige Texte (MAKT): aus texts[] die richtige Sprache waehlen.
  const texts = Array.isArray(p.texts) ? p.texts : [];
  const localized = pickTexts(texts, currentLang);
  const resolvedName = (localized && localized.name) || p.name || '';
  const resolvedDescription = (localized && localized.description) || p.description || '';

  // Multi-Lagerort (MARD): stock[]-Array auf DTO-Format mappen.
  const stockArray = Array.isArray(p.stock) ? p.stock : [];
  let stockByLocation;
  if (stockArray.length > 0) {
    stockByLocation = stockArray.map((s) => ({
      plant: s.plant || '',
      storageLocation: s.storage_location || '',
      locationName: s.location_label || '',
      plantName: s.plant_label || '',
      qty: toNum(s.qty) ?? 0,
      status: normStockStatus(s.status, s.qty),
      stockType: s.stock_type || 'own',
      vendor: s.vendor || '',
      specialStockInd: s.special_stock_ind || '',
    }));
  } else {
    // Fallback: Legacy-Einzelbestand aus stock_quantity/plant
    const stockQty = toNum(p.stock_quantity);
    const hasLegacyStock =
      p.stock_quantity !== undefined && p.stock_quantity !== null && p.stock_quantity !== '';
    stockByLocation = hasLegacyStock
      ? [
          {
            // Kein Ersatz-Werk: ein Bestand unter einem erfundenen Werk anzuzeigen ist
            // falscher als einer ohne Werk. 1000 stammt aus SBX und existiert auf S23 nicht.
            plant: p.plant || '',
            storageLocation: '',
            locationName: '',
            plantName: '',
            qty: stockQty ?? 0,
            status: (stockQty ?? 0) > 0 ? 'green' : 'red',
            stockType: 'own',
            vendor: '',
            specialStockInd: '',
          },
        ]
      : [];
  }

  const hasAvailability = stockByLocation.length > 0;

  // Bezugsquellen (product_sources + product_price_scales): primäre zuerst sortiert.
  const rawSources = Array.isArray(p.sources) ? p.sources : [];
  const mappedSources = rawSources.map(mapSource);
  mappedSources.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
  const primarySource = mappedSources.find((s) => s.isPrimary) || mappedSources[0] || null;

  // Bestellhistorie (product_order_history, READ-ONLY SAP-Sync): DESC nach orderDate geliefert.
  const rawOrderHistory = Array.isArray(p.order_history) ? p.order_history : [];
  const mappedOrderHistory = rawOrderHistory.map(mapOrderHistory);

  return {
    source: p.source || 'internal',
    editable: p.source !== 'sap',

    identity: {
      status: 'loaded',
      name: resolvedName,
      description: resolvedDescription,
      sku: p.sku || p.vendor_mat || '',
      sapMaterialNo: p.sku || '',
      ean: p.ean || '',
      manufacturer: p.manufacturer || '',
      manufacturerPartNo: p.manufacturer_part_no || p.vendor_mat || '',
      materialType: p.material_type || '',
      materialStatus: p.status || 'active',
      materialGroup: p.category || '',
      materialGroupLabel: p.category_label || '',
      baseUnit: p.unit || 'STK',
      grossWeight: toNum(p.gross_weight),
      netWeight: toNum(p.net_weight),
      weightUnit: p.weight_unit || '',
      sizeText: p.size_text || '',
      profitCenter: p.profit_center || '',
      images: p.image_url ? [p.image_url] : [],
      texts,
    },

    technical: (() => {
      // Bevorzuge child-table attributes[] (product_attributes, strukturiert).
      // Fallback auf JSON-Spalte technical_attributes (Rueckwaertskompatibilitaet).
      const childAttrs = Array.isArray(p.attributes) && p.attributes.length > 0
        ? p.attributes.map((a) => ({
            label: a.label || a.char_name || '',
            value: a.value || '',
            unit: a.unit || '',
            char_group: a.char_group || '',
          }))
        : null;
      const resolvedAttrs = childAttrs !== null ? childAttrs
        : (Array.isArray(techAttrs) ? techAttrs : []);
      return {
        status: resolvedAttrs.length > 0 ? 'loaded' : 'demo',
        attributes: resolvedAttrs,
      };
    })(),

    procurement: {
      status: 'loaded',
      price: {
        netPrice: toNum(p.price) ?? 0,
        currency: p.currency || 'EUR',
        priceUnit: toNum(p.price_unit) ?? 1,
        orderUnit: p.unit || 'STK',
        source: p.price_source || 'internal',
        validFrom: contract.validFrom || null,
        validTo: contract.validTo || null,
        scales: Array.isArray(scales) ? scales : [],
      },
      // Rückwärtskompatibilität: preferredVendor aus primärer Bezugsquelle (wenn vorhanden),
      // sonst Legacy-Feld preferred_vendor.
      preferredVendor: primarySource
        ? { number: primarySource.vendor, name: primarySource.vendorName }
        : p.preferred_vendor ? { number: '', name: p.preferred_vendor } : null,
      fixedVendor: p.fixed_vendor ? { number: '', name: p.fixed_vendor } : null,
      purchasingOrg: p.purchasing_org || '',
      purchasingGroup: p.purchasing_group || '',
      plannedDeliveryTime: p.planned_delivery_time || '',
      minOrderQty: toNum(p.min_order_quantity) ?? 1,
      // Rückwärtskompatibilität: infoRecord aus primärer Bezugsquelle.
      infoRecord: primarySource && primarySource.infoRecordNo
        ? { number: primarySource.infoRecordNo, exists: true }
        : { number: '', exists: false },
      contract: contract && contract.exists ? contract : { exists: false },
      // Bezugsquellen-Liste (neu, additiv).
      sources: mappedSources,
      // Bestellhistorie (READ-ONLY, SAP-Sync, DESC nach Datum).
      orderHistory: mappedOrderHistory,
    },

    availability: hasAvailability
      ? { status: 'loaded', stockByLocation }
      : { status: 'demo', stockByLocation: [] },

    documents: Array.isArray(docs) && docs.length > 0
      ? { status: 'loaded', items: docs }
      : { status: 'not_available', items: [] },

    compliance: Array.isArray(comp) && comp.length > 0
      ? { status: 'loaded', items: comp }
      : { status: 'not_available', items: [] },

    actions: { quantity: 1, addToCartEnabled: p.source !== 'sap' || p.status !== 'blocked' },
  };
};

// Referenz-/Test-DTO (voll befüllt) — nur für Entwicklung/Tests, nicht in der UI verdrahtet.
export const MOCK_PRODUCT_DTO = {
  source: 'sap',
  editable: false,
  identity: {
    status: 'loaded',
    name: 'Durchflussregler DN50 PN16',
    description: 'Kompakter Durchflussregler für industrielle Medien, DN50 / PN16, mit HART-Schnittstelle.',
    sku: 'DFR-50-16',
    sapMaterialNo: '100023145',
    manufacturer: 'FlowTec',
    manufacturerPartNo: 'FT-DN50-PN16',
    materialType: 'HAWA',
    materialGroup: 'Armaturen',
    baseUnit: 'ST',
    images: [],
  },
  technical: {
    status: 'loaded',
    attributes: [
      { label: 'Nennweite', value: 'DN50', unit: '', group: 'Mechanik' },
      { label: 'Durchflussrate', value: '0,8–18', unit: 'm³/h', group: 'Hydraulik' },
    ],
  },
  procurement: {
    status: 'loaded',
    price: { netPrice: 1245.0, currency: 'EUR', priceUnit: 1, orderUnit: 'ST', source: 'contract', validFrom: '2026-01-01', validTo: '2026-12-31', scales: [{ fromQty: 5, price: 1180.0 }] },
    preferredVendor: { number: '0007075136', name: 'FlowTec Vertrieb GmbH' },
    fixedVendor: null,
    purchasingOrg: '1000',
    purchasingGroup: '101',
    plannedDeliveryTime: '5 Tage',
    minOrderQty: 1,
    infoRecord: { number: '5300004711', exists: true },
    contract: { exists: true, number: '4600000123', validFrom: '2026-01-01', validTo: '2026-12-31' },
    orderHistory: [
      { orderNo: '4500123456', orderItem: '00010', orderDate: '2026-04-23', vendor: '0007075136', vendorName: 'FlowTec Vertrieb GmbH', quantity: 5, unit: 'ST', netPrice: 1245.0, priceUnit: 1, netValue: 6225.0, currency: 'EUR', plant: '1000', docType: 'NB' },
      { orderNo: '4500118900', orderItem: '00010', orderDate: '2026-01-15', vendor: '0007075136', vendorName: 'FlowTec Vertrieb GmbH', quantity: 2, unit: 'ST', netPrice: 1245.0, priceUnit: 1, netValue: 2490.0, currency: 'EUR', plant: '1000', docType: 'NB' },
      { orderNo: '4500112345', orderItem: '00020', orderDate: '2025-10-03', vendor: '0007076525', vendorName: 'Armaturentechnik Süd GmbH', quantity: 3, unit: 'ST', netPrice: 1310.0, priceUnit: 1, netValue: 3930.0, currency: 'EUR', plant: '1000', docType: 'NB' },
    ],
    sources: [
      {
        vendor: '0007075136',
        vendorName: 'FlowTec Vertrieb GmbH',
        vendorMaterialNo: 'FT-DN50-PN16-A',
        infoRecordNo: '5300004711',
        purchasingOrg: '1000',
        purchasingGroup: '101',
        netPrice: 1245.0,
        currency: 'EUR',
        priceUnit: 1,
        orderUnit: 'ST',
        minOrderQty: 1,
        plannedDeliveryTime: 5,
        incoterms: 'DDP',
        isPrimary: true,
        scales: [
          { minQty: 1, amount: 1245.0, currency: 'EUR', priceUnit: 1 },
          { minQty: 5, amount: 1180.0, currency: 'EUR', priceUnit: 1 },
          { minQty: 20, amount: 1100.0, currency: 'EUR', priceUnit: 1 },
        ],
      },
      {
        vendor: '0007076525',
        vendorName: 'Armaturentechnik Süd GmbH',
        vendorMaterialNo: 'ATS-DN50-V2',
        infoRecordNo: '5300004712',
        purchasingOrg: '1000',
        purchasingGroup: '101',
        netPrice: 1310.0,
        currency: 'EUR',
        priceUnit: 1,
        orderUnit: 'ST',
        minOrderQty: 1,
        plannedDeliveryTime: 15,
        incoterms: 'FCA',
        isPrimary: false,
        scales: [
          { minQty: 1, amount: 1310.0, currency: 'EUR', priceUnit: 1 },
        ],
      },
    ],
  },
  availability: { status: 'loaded', stockByLocation: [
    { plant: '1000', storageLocation: '1100', locationName: 'Werk Nord', qty: 42, status: 'green', stockType: 'own', vendor: '', specialStockInd: '' },
    { plant: '1000', storageLocation: '', locationName: '', qty: 33, status: 'green', stockType: 'at_vendor', vendor: '0007074189', specialStockInd: 'O' },
  ] },
  documents: { status: 'loaded', items: [{ type: 'datasheet', title: 'Datenblatt', fileFormat: 'PDF', sizeMb: 2.4, url: '' }] },
  compliance: { status: 'loaded', items: [{ label: 'ISO 9001:2015' }] },
  actions: { quantity: 1, addToCartEnabled: true },
};
